import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { Auction } from '../../../auction/auction.model';
import { AuctionService } from '../../../auction/auction.service';
import { BidCutRequestCalculationService } from '../../../hammer/hammer-bid-cut-request-calculation.service';
import { LotData } from '../../../panel/panel.model';
import { ActionLoaderComponent } from '../../action-loader/action-loader.component';
import { AppParamItems, AppParamsService } from '../../app-params.service';
import { CardTemplateConfig } from '../card-template/card-template.component';
import { CURRENCY_TYPES, CurrencyType, DEFAULT_CURRENCY_FORMAT } from '../../currency/currency-types';
import { integerValidator } from '../../form-validators/integer.validator';
import { parseToNumber } from '../../funcs/parser-helpers';
import { NotificationService } from '../../notification.service';

@Component({
  selector: 'app-auction-cut-bid',
  templateUrl: './auction-cut-bid.component.html',
  styleUrls: ['./auction-cut-bid.component.scss']
})
export class AuctionCutBidComponent implements OnInit, OnChanges, OnDestroy {

  @Input() currentAuction: Auction;
  @Input() lotData: LotData;
  @Input() primaryCurrencyType: CurrencyType = CurrencyType.COP;

  @ViewChild('cutLoader')
  public cutLoader: ActionLoaderComponent;

  public calculatedValue: number = 0;
  public currentValue: number = 0;
  public cutBidForm: FormGroup;
  public isCutEnabled: boolean = true;

  private readonly cardClass: string = 'auction-cut-bid-card';
  private readonly compact: boolean = true;
  private readonly destroy$ = new Subject<void>();
  private readonly title: string = 'Partir puja';
  private lotCutPercentage: number = 0;

  public get allowsDecimal(): boolean {
    return CURRENCY_TYPES.get(this.primaryCurrencyType)?.requiresDecimal ?? false;
  }

  public get moneyNumberFormat(): string {
    return CURRENCY_TYPES.get(this.primaryCurrencyType)?.format ?? DEFAULT_CURRENCY_FORMAT;
  }

  constructor(
    private readonly appParamsService: AppParamsService,
    private readonly auctionService: AuctionService,
    private readonly bidCutCalculationService: BidCutRequestCalculationService,
    private readonly fb: FormBuilder,
    private readonly notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['primaryCurrencyType'] && this.cutBidForm) {
      this.applyAmountValidators();
      this.setDefaultAmount();
      this.recalculateValue();
    }
    if (changes['lotData']?.currentValue) {
      this.updateLotData();
    }
    if (changes['currentAuction']?.currentValue) {
      this.loadCutPercentage();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.appParamsService.dispose(AppParamItems.AppParamLots.lotCutPercentage);
  }

  public setCutBidLoading(loading: boolean): void {
    if (!this.cutLoader) {
      return;
    }
    if (loading) {
      this.cutLoader.start();
    } else {
      this.cutLoader.stop();
    }
  }

  async onCutBid(): Promise<void> {
    if (!this.canCutBid()) {
      return;
    }

    this.cutLoader.start();

    try {
      const amount = this.cutBidForm.get('amount')?.value;
      const bidBeforeRequest = this.currentValue;
      let numericCutAmount = parseToNumber(amount, 0);
      if (!this.allowsDecimal) {
        numericCutAmount = Math.round(numericCutAmount);
      }
      await this.auctionService.cutBid(this.currentAuction.id, numericCutAmount);

      this.handleSuccessfulCut(numericCutAmount, bidBeforeRequest);
    } catch (error) {
      this.notificationService.error(error);
    } finally {
      this.cutLoader.stop();
    }
  }

  get cardConfig(): CardTemplateConfig {
    return {
      title: this.title,
      cardClass: this.cardClass,
      compact: this.compact
    };
  }

  private calculateDefaultAmount(): number {
    return this.bidCutCalculationService.calculateCutPercentageAmount(
      this.currentValue,
      this.lotCutPercentage,
      this.allowsDecimal
    );
  }

  private canCutBid(): boolean {
    return this.cutBidForm.valid && this.isCutEnabled;
  }

  private canSetDefaultAmount(): boolean {
    return this.isCutEnabled && this.currentValue > 0 && this.lotCutPercentage > 0;
  }

  private handleSuccessfulCut(amount: number, bidBeforeRequest: number): void {
    this.notificationService.success('Puja cortada exitosamente');
    this.updateCurrentValue(amount, bidBeforeRequest);
    this.cutBidForm.reset();
    this.setDefaultAmount();
  }

  private initializeComponent(): void {
    this.initializeForm();
    this.setupFormSubscription();
    this.updateLotData();

    if (this.currentAuction) {
      this.loadCutPercentage();
    }
  }

  private initializeForm(): void {
    this.cutBidForm = this.fb.group({
      amount: ['', Validators.required]
    });
    this.applyAmountValidators();
  }

  private applyAmountValidators(): void {
    const ctrl = this.cutBidForm?.get('amount');
    if (!ctrl) {
      return;
    }
    const validators: ValidatorFn[] = [Validators.required];
    if (this.allowsDecimal) {
      validators.push(Validators.min(0.01));
    } else {
      validators.push(Validators.min(1), integerValidator);
    }
    ctrl.setValidators(validators);
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private loadCutPercentage(): void {
    if (!this.currentAuction?.id) {
      return;
    }

    this.appParamsService
      .watchFor(this.currentAuction.id, AppParamItems.AppParamLots.lotCutPercentage)
      .pipe(
        map((percentage: number) => percentage || 0),
        takeUntil(this.destroy$)
      )
      .subscribe(percentage => {
        this.lotCutPercentage = percentage;
        this.isCutEnabled = percentage > 0;
        this.setDefaultAmount();
      });
  }

  private recalculateValue(): void {
    if (!this.cutBidForm) {
      return;
    }
    const amount = this.cutBidForm.get('amount')?.value;
    const parsedAmount = amount && !isNaN(amount) ? parseFloat(amount) : 0;
    this.calculatedValue = this.currentValue - parsedAmount;
  }

  private setDefaultAmount(): void {
    if (!this.cutBidForm) {
      return;
    }
    if (this.canSetDefaultAmount()) {
      const defaultAmount = this.calculateDefaultAmount();
      const formatted = this.allowsDecimal ? defaultAmount.toFixed(2) : String(Math.round(defaultAmount));
      this.cutBidForm.get('amount')?.setValue(formatted);
      this.recalculateValue();
    }
  }

  private setupFormSubscription(): void {
    this.cutBidForm.get('amount')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.recalculateValue());
  }

  private updateCurrentValue(amount: number, bidBeforeRequest: number): void {
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
    const parsedLotBid =
      this.lotData?.bid != null && String(this.lotData.bid).trim() !== ''
        ? parseFloat(String(this.lotData.bid))
        : NaN;
    const expectedAfterCut = bidBeforeRequest - numericAmount;
    const preRequestTolerance = this.allowsDecimal ? 0.01 : 0.5;
    const lotBidDiffersFromPreRequest =
      !Number.isNaN(parsedLotBid) && Math.abs(parsedLotBid - bidBeforeRequest) > preRequestTolerance;
    const resolved = lotBidDiffersFromPreRequest
      ? parsedLotBid
      : !Number.isNaN(expectedAfterCut)
        ? expectedAfterCut
        : this.currentValue;
    this.currentValue = resolved;
    this.calculatedValue = resolved;
  }

  private updateLotData(): void {
    this.currentValue = this.lotData?.bid ? parseFloat(this.lotData.bid) : 0;
    this.calculatedValue = this.currentValue;
    this.setDefaultAmount();
  }
}
